import React, { useState, useEffect, useRef } from 'react';
import { GraduationCap, LogOut, Users, BookOpen, DollarSign, TrendingUp, Plus, Trash2, Upload, Edit2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = '';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [tutorProfiles, setTutorProfiles] = useState([]);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', subjects: '', languages: '', bio: '', years_experience: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, sessionsRes, profilesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/sessions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tutor-profiles/all`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSessions(sessionsRes.data);
      setTutorProfiles(profilesRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {
        name: profileForm.name,
        subjects: profileForm.subjects.split(',').map(s => s.trim()).filter(Boolean),
        languages: profileForm.languages.split(',').map(s => s.trim()).filter(Boolean),
        bio: profileForm.bio,
        years_experience: parseInt(profileForm.years_experience) || 0
      };

      if (editingProfile) {
        await axios.put(`${BACKEND_URL}/api/tutor-profiles/${editingProfile.profile_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/tutor-profiles`, payload, { withCredentials: true });
      }
      setShowProfileForm(false);
      setEditingProfile(null);
      setProfileForm({ name: '', subjects: '', languages: '', bio: '', years_experience: '' });
      fetchData();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this tutor profile?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tutor-profiles/${profileId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert('Failed to delete profile');
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm({
      name: profile.name,
      subjects: profile.subjects?.join(', ') || '',
      languages: profile.languages?.join(', ') || '',
      bio: profile.bio || '',
      years_experience: String(profile.years_experience || '')
    });
    setShowProfileForm(true);
  };

  const handlePhotoUpload = async (profileId, file) => {
    setUploadingPhoto(profileId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${BACKEND_URL}/api/tutor-profiles/${profileId}/photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleApproveProfile = async (profileId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/tutor-profiles/${profileId}`, { status: 'approved' }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error approving profile:', error);
      alert('Failed to approve profile');
    }
  };

  const handleRejectProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to reject this profile?')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/tutor-profiles/${profileId}`, { status: 'rejected' }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error rejecting profile:', error);
      alert('Failed to reject profile');
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/send-reminders`, {}, { withCredentials: true });
      alert(data.message || 'Reminders sent successfully!');
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert('Failed to send reminders. Make sure email service is configured.');
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold">Admin: {user.name}</span>
              <button
                onClick={logout}
                className="flex items-center gap-2 text-sm font-semibold px-4 py-2 border border-slate-900 rounded-md hover:bg-slate-900 hover:text-white"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight mb-2">Admin Dashboard</h1>
              <p className="text-slate-600">Platform overview and management</p>
            </div>
            <button
              onClick={handleSendReminders}
              disabled={sendingReminders}
              className="px-6 py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c] disabled:opacity-50"
            >
              {sendingReminders ? 'Sending...' : 'Send Session Reminders'}
            </button>
          </div>
        </div>

        <div className="flex gap-4 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'overview' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('tutors')}
            className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'tutors' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
            data-testid="tab-tutor-profiles"
          >
            Tutor Profiles
          </button>
        </div>

        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-[#002FA7]" />
                </div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-1">Students</div>
                <div className="text-3xl font-black">{stats?.total_students || 0}</div>
              </div>

              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <GraduationCap className="w-8 h-8 text-[#E32636]" />
                </div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-1">Tutors</div>
                <div className="text-3xl font-black">{stats?.total_tutors || 0}</div>
              </div>

              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <BookOpen className="w-8 h-8 text-[#FFC000]" />
                </div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-1">Sessions</div>
                <div className="text-3xl font-black">{stats?.total_sessions || 0}</div>
              </div>

              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-sm font-semibold tracking-[0.2em] uppercase text-slate-600 mb-1">Revenue</div>
                <div className="text-3xl font-black">${stats?.total_revenue?.toFixed(2) || '0.00'}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-xl font-bold">All Users</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F3F4F6]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u._id} className="border-b border-slate-200">
                          <td className="px-6 py-3">{u.name}</td>
                          <td className="px-6 py-3">
                            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                              u.role === 'admin' ? 'bg-[#E32636] text-white' :
                              u.role === 'tutor' ? 'bg-[#002FA7] text-white' :
                              'bg-slate-100 text-slate-800'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm text-slate-600">{u.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-xl font-bold">Recent Sessions</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F3F4F6]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-[0.2em] uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.slice(0, 10).map(session => (
                        <tr key={session.id} className="border-b border-slate-200">
                          <td className="px-6 py-3">{session.subject}</td>
                          <td className="px-6 py-3">{session.date}</td>
                          <td className="px-6 py-3">
                            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                              session.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              session.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-slate-100 text-slate-800'
                            }`}>
                              {session.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {sessions.length === 0 && (
                        <tr>
                          <td colSpan="3" className="px-6 py-8 text-center text-slate-600">
                            No sessions yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'tutors' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Manage Tutor Profiles</h2>
              <button
                onClick={() => { setEditingProfile(null); setProfileForm({ name: '', subjects: '', languages: '', bio: '', years_experience: '' }); setShowProfileForm(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-semibold rounded-md hover:bg-[#001a5c]"
                data-testid="add-tutor-profile-btn"
              >
                <Plus className="w-4 h-4" /> Add Tutor
              </button>
            </div>

            {showProfileForm && (
              <div className="bg-white border border-slate-200 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">{editingProfile ? 'Edit Tutor Profile' : 'New Tutor Profile'}</h3>
                  <button onClick={() => { setShowProfileForm(false); setEditingProfile(null); }} className="text-slate-400 hover:text-slate-900">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">Name</label>
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      placeholder="e.g. Sophie Martin"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Years of Experience</label>
                    <input
                      type="number"
                      value={profileForm.years_experience}
                      onChange={(e) => setProfileForm({ ...profileForm, years_experience: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      placeholder="e.g. 10"
                      data-testid="profile-experience-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Subjects (comma-separated)</label>
                    <input
                      type="text"
                      value={profileForm.subjects}
                      onChange={(e) => setProfileForm({ ...profileForm, subjects: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      placeholder="e.g. French, English, ESL"
                      data-testid="profile-subjects-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Languages (comma-separated)</label>
                    <input
                      type="text"
                      value={profileForm.languages}
                      onChange={(e) => setProfileForm({ ...profileForm, languages: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      placeholder="e.g. French, English, Creole"
                      data-testid="profile-languages-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-1">Bio</label>
                    <textarea
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      placeholder="Short biography about the tutor..."
                      data-testid="profile-bio-input"
                    />
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleSaveProfile}
                    disabled={savingProfile || !profileForm.name}
                    className="px-6 py-2 bg-[#002FA7] text-white font-semibold rounded-md hover:bg-[#001a5c] disabled:opacity-50"
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? 'Saving...' : editingProfile ? 'Update Profile' : 'Create Profile'}
                  </button>
                  <button
                    onClick={() => { setShowProfileForm(false); setEditingProfile(null); }}
                    className="px-6 py-2 border border-slate-300 text-slate-700 font-semibold rounded-md hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {tutorProfiles.length === 0 ? (
              <div className="bg-white border border-slate-200 p-12 text-center">
                <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-2">No tutor profiles yet</p>
                <p className="text-sm text-slate-400">Click "Add Tutor" to create the first profile</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tutorProfiles.map((profile) => (
                  <div key={profile.profile_id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`admin-tutor-card-${profile.profile_id}`}>
                    <div className="h-48 bg-slate-100 relative overflow-hidden">
                      {profile.photo_url ? (
                        <img src={profile.photo_url} alt={profile.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#002FA7]/10 to-[#002FA7]/5">
                          <span className="text-5xl font-black text-[#002FA7]/20">{profile.name?.charAt(0) || 'T'}</span>
                        </div>
                      )}
                      <label className="absolute bottom-2 right-2 cursor-pointer bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-white">
                        <Upload className="w-3 h-3" />
                        {uploadingPhoto === profile.profile_id ? 'Uploading...' : 'Photo'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => { if (e.target.files[0]) handlePhotoUpload(profile.profile_id, e.target.files[0]); }}
                          data-testid={`upload-photo-${profile.profile_id}`}
                        />
                      </label>
                      {profile.status && profile.status !== 'approved' && (
                        <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold ${
                          profile.status === 'pending' ? 'bg-yellow-400 text-yellow-900' : 'bg-red-500 text-white'
                        }`}>
                          {profile.status.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold mb-1">{profile.name}</h3>
                      <p className="text-xs text-slate-500 mb-2">{profile.years_experience}+ yrs | {profile.subjects?.join(', ')}</p>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{profile.bio}</p>
                      {profile.status === 'pending' && (
                        <div className="flex gap-2 mb-3">
                          <button
                            onClick={() => handleApproveProfile(profile.profile_id)}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded hover:bg-green-700"
                            data-testid={`approve-profile-${profile.profile_id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectProfile(profile.profile_id)}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded hover:bg-red-700"
                            data-testid={`reject-profile-${profile.profile_id}`}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditProfile(profile)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded hover:bg-slate-50"
                          data-testid={`edit-profile-${profile.profile_id}`}
                        >
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProfile(profile.profile_id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border border-red-200 text-red-600 rounded hover:bg-red-50"
                          data-testid={`delete-profile-${profile.profile_id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
